<template>
  <div>
    <div class="tw-relative">
      <slot name="header" />

      <div
        ref="track"
        class="tw-bg-black/5 tw-rounded-full tw-relative tw-h-3"
        @click.stop="value = mapFromScreen($event.screenX)"
      >
        <slot name="track" />

        <div
          :id="id"
          ref="knob"
          role="slider"
          :aria-label="label"
          aria-orientation="horizontal"
          :aria-valuemin="min"
          :aria-valuemax="max"
          :aria-valuenow="value"
          :aria-valuetext="valueLabel"
          class="tw-absolute tw-cursor-pointer tw-bg-gray-700 tw-rounded-full tw-h-full tw-p-0 tw-aspect-square tw-select-none tw-origin-center tw-transition hover:tw-scale-150 focus-visible:tw-ring-2 tw-ring-offset-2 tw-outline-none"
          :class="{ 'tw-scale-150': knobIsActiveElement }"
          tabindex="0"
          :style="{ left: `${knobX}px` }"
          @keyup.left="value -= stepValue"
          @keyup.down="value -= stepValue * 10"
          @keyup.right="value += stepValue"
          @keyup.up="value += stepValue * 10"
          @keyup.home.prevent.stop="value = min"
          @keyup.end.prevent.stop="value = max"
          @keydown.left.prevent
          @keydown.down.prevent
          @keydown.right.prevent
          @keydown.up.prevent
          @keydown.home.prevent
          @keydown.end.prevent
        />
      </div>

      <template v-if="tickData">
        <div class="tw-absolute tw-top-full tw-inset-x-0 tw-z-10">
          <div
            v-for="({ tick, position }, index) in tickData"
            :key="index"
            :title="tick.label"
            :aria-label="tick.label"
            class="tw-absolute tw-flex tw-flex-col tw-items-center tw-mt-1 tw-gap-1"
            :style="{
              left: `${position}px`,
              transform: `translateX(calc(-50% + ${trackHeight / 2}px))`,
            }"
          >
            <div class="tw-h-1 tw-w-px tw-bg-black/30" />
            <slot name="tick" :tick="tick" :index="index" />
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script lang="ts">
type Tick = {
  value: number
  label?: string
}
</script>

<script lang="ts" setup generic="TickType extends Tick">
import { useActiveElement, useDraggable, useElementBounding, useVModel } from '@vueuse/core'
import { computed, ref } from 'vue'
import { mapToDomain } from './util'

const props = defineProps<{
  modelValue: number
  min: number
  max: number
  id?: string
  ticks?: TickType[]
  step?: number | 'any'
  label?: string
  valueLabel?: string
}>()
const emit = defineEmits<{
  (e: 'update:modelValue', value: number): void
}>()
defineSlots<{
  tick?: (props: { index: number; tick: TickType }) => any
  header?: (props: Record<string, never>) => any
  track?: (props: Record<string, never>) => any
}>()

const value = useVModel(props, 'modelValue', emit)
const stepValue = computed<number>(() =>
  props.step === undefined || props.step === 'any' ? 1 : props.step,
)
const activeElement = useActiveElement()
const track = ref<HTMLElement>()
const knob = ref<HTMLElement>()
const knobIsActiveElement = computed(() => knob.value === activeElement.value)
const { width: trackWidth, height: trackHeight, left: trackLeft } = useElementBounding(track)
const knobX = computed(() => mapToTrack(value.value))
const tickData = computed(() => {
  if (!props.ticks) return undefined
  return props.ticks.map((tick) => ({
    tick,
    position: mapToTrack(tick.value),
  }))
})
useDraggable(knob, {
  axis: 'x',
  stopPropagation: true,
  onMove({ x }) {
    if (knobIsActiveElement.value) {
      value.value = mapFromScreen(x)
    }
  },
})

function mapFromScreen(x: number) {
  return mapToDomain(x - trackLeft.value, [0, trackWidth.value], [props.min, props.max])
}

function mapToTrack(value: number) {
  return mapToDomain(value, [props.min, props.max], [0, trackWidth.value - trackHeight.value])
}
</script>
